import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop } from "../utils";
import { terminate, saveNodeType, isHoverForInteractiveType } from "./utils";
import EachCalculoRG from "./wrapper/EachCalculoRG";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";
import { RG_DEFAULT_LEVELS } from "./calculoRGMath";

// Cálculo R/G — migrado do Silverlight (CalculoRGObject). Dois pontos:
// P0 = entrada (nível 0), P1 = stop (nível -1); linhas horizontais em
// múltiplos do risco projetadas à direita, com rótulo preço (delta %).
// Padrão de desenho odin: Click + Drag + Release (gesto natural: arrastar
// da entrada até o stop, tipicamente na vertical).
class CalculoRG extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawCalculoRG = this.handleDrawCalculoRG.bind(this);

    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.getSelectionState = isHoverForInteractiveType("calculosRG").bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = [];

    this.state = {};
  }
  handleDrawCalculoRG(xyValue) {
    const { current } = this.state;
    if (isDefined(current) && isDefined(current.x1)) {
      this.mouseMoved = true;
      this.setState({
        current: {
          ...current,
          x2: xyValue[0],
          y2: xyValue[1],
        },
      });
    }
  }
  handleDrag(index, xy) {
    this.setState({
      override: {
        index,
        ...xy,
      },
    });
  }
  handleDragComplete(moreProps) {
    const { calculosRG } = this.props;
    const { override } = this.state;
    if (isDefined(override)) {
      const { index, ...rest } = override;

      const newObjects = calculosRG.map((each, idx) =>
        idx === index ? { ...each, ...rest, selected: true } : each,
      );
      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newObjects, moreProps);
        },
      );
    }
  }
  handleStart(xyValue, moreProps) {
    const { current } = this.state;
    if (isNotDefined(current) || isNotDefined(current.x1)) {
      this.mouseMoved = false;
      this.setState(
        {
          current: {
            x1: xyValue[0],
            y1: xyValue[1],
            x2: null,
            y2: null,
          },
        },
        () => {
          this.props.onStart(moreProps);
        },
      );
    }
  }
  handleEnd(xyValue, moreProps, e) {
    const { calculosRG, appearance, type } = this.props;
    const { current } = this.state;

    if (this.mouseMoved && isDefined(current) && isDefined(current.x1)) {
      const newObjects = calculosRG.concat({
        ...current,
        x2: xyValue[0],
        y2: xyValue[1],
        selected: true,
        appearance,
        type,
      });

      this.setState(
        {
          current: null,
        },
        () => {
          this.props.onComplete(newObjects, moreProps, e);
        },
      );
    }
  }
  render() {
    const { current, override } = this.state;
    const { calculosRG, appearance, type } = this.props;
    const {
      currentPositionStroke,
      currentPositionOpacity,
      currentPositionStrokeWidth,
      currentPositionRadius,
    } = this.props;

    const { enabled, hoverText } = this.props;
    const overrideIndex = isDefined(override) ? override.index : null;
    const hoverTextWithDefault = {
      ...CalculoRG.defaultProps.hoverText,
      ...hoverText,
    };

    const currentObject =
      isDefined(current) && isDefined(current.x2) ? (
        <EachCalculoRG
          interactive={false}
          type={type}
          appearance={appearance}
          hoverText={hoverTextWithDefault}
          {...current}
        />
      ) : null;

    return (
      <g>
        {calculosRG.map((each, idx) => {
          const eachAppearance = isDefined(each.appearance)
            ? { ...appearance, ...each.appearance }
            : appearance;

          const eachHoverText = isDefined(each.hoverText)
            ? { ...hoverTextWithDefault, ...each.hoverText }
            : hoverTextWithDefault;

          return (
            <EachCalculoRG
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              type={each.type}
              selected={each.selected}
              {...(idx === overrideIndex ? override : each)}
              hoverText={eachHoverText}
              appearance={eachAppearance}
              onDrag={this.handleDrag}
              onDragComplete={this.handleDragComplete}
            />
          );
        })}
        {currentObject}
        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onClick={this.handleEnd}
          onMouseMove={this.handleDrawCalculoRG}
          data-evo-id="interactive-calculo-rg"
        />
      </g>
    );
  }
}

CalculoRG.propTypes = {
  enabled: PropTypes.bool.isRequired,

  onStart: PropTypes.func,
  onComplete: PropTypes.func,
  onSelect: PropTypes.func,

  type: PropTypes.string,
  hoverText: PropTypes.object.isRequired,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,

  calculosRG: PropTypes.array.isRequired,

  appearance: PropTypes.shape({
    stroke: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
      PropTypes.string,
    ]),
    levels: PropTypes.array,
    font: PropTypes.shape({
      fontFamily: PropTypes.string,
      fontSize: PropTypes.number,
      color: PropTypes.string,
    }),
    labelUsaCorDaLinha: PropTypes.bool,
    r: PropTypes.number,
  }).isRequired,
};

CalculoRG.defaultProps = {
  enabled: true,
  type: "RAY",
  calculosRG: [],

  onStart: noop,
  onComplete: noop,
  onSelect: noop,

  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique para selecionar",
    selectedText: "",
  },
  currentPositionStroke: "#000000",
  currentPositionOpacity: 1,
  currentPositionStrokeWidth: 3,
  currentPositionRadius: 4,

  appearance: {
    stroke: { color: "#FFFFFF", width: 1, opacity: 1, dash: "Solid" },
    levels: RG_DEFAULT_LEVELS,
    font: {
      fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
      fontSize: 10,
      color: "#FFFFFF",
    },
    labelUsaCorDaLinha: true,
    r: 6,
  },
};

export default CalculoRG;
